import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Sparkles, Code2, Loader2 } from "lucide-react";
import type { SourceItem } from "../AITutorSources";

/** Kept for call-site compatibility; animation ignores these fields. */
export interface PipelineSections {
  sql?: string;
  sources?: SourceItem[];
  study_materials?: unknown[];
}

const PHASE_MS = 3000;

const PHASES = [
  {
    key: "sources",
    label: "Исследование источников",
    sub: "Поиск релевантной документации",
    Icon: Search,
  },
  {
    key: "practices",
    label: "Поиск лучших практик",
    sub: "Анализ примеров и паттернов",
    Icon: Sparkles,
  },
  {
    key: "materials",
    label: "Подготовка практических материалов",
    sub: "Генерация упражнений и SQL",
    Icon: Code2,
  },
] as const;

const MAX_PREVIEW_LINES = 4;

/**
 * Looped status-rotation loader for lesson generation.
 * Shows real-time LLM preview tokens in a streaming text block.
 */
export function TopicGenerationPipeline({ theoryTokens, previewTokens }: {
  theoryTokens?: string;
  previewTokens?: string;
  sections?: PipelineSections;
}) {
  const [active, setActive] = useState(0);
  const [fill, setFill] = useState(0);

  useEffect(() => {
    const t0 = performance.now();
    let raf = 0;
    const tick = () => {
      const elapsed = performance.now() - t0;
      const total = elapsed % (PHASE_MS * PHASES.length);
      setActive(Math.floor(total / PHASE_MS) % PHASES.length);
      setFill((total % PHASE_MS) / PHASE_MS);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  const phase = PHASES[active];

  // Show real streaming text: prefer previewTokens (live LLM), fallback to theoryTokens
  const rawStream = previewTokens || theoryTokens || "";

  // Take last N lines to keep the block compact
  const streamDisplay = useMemo(() => {
    if (!rawStream) return "";
    const lines = rawStream.split("\n");
    if (lines.length <= MAX_PREVIEW_LINES) return rawStream;
    return lines.slice(-MAX_PREVIEW_LINES).join("\n");
  }, [rawStream]);

  const hasStream = streamDisplay.length > 0;

  return (
    <motion.div
      className="w-full max-w-sm mx-auto rounded-2xl border border-neutral-border/60 bg-white shadow-sm overflow-hidden"
      role="status"
      aria-live="polite"
      aria-label="Идёт сборка урока"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: "easeOut" }}
    >
      {/* Header */}
      <div className="px-5 pt-4 pb-3">
        <div className="flex items-center gap-2.5">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary/60" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-primary" />
          </span>
          <h3 className="text-sm font-semibold text-neutral-text tracking-tight">
            Собираем урок
          </h3>
        </div>
      </div>

      {/* Active phase — single slot, content rotates */}
      <div className="px-5 pb-4">
        <AnimatePresence mode="wait">
          <motion.div
            key={phase.key}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.25, ease: "easeInOut" }}
            className="flex items-start gap-3"
          >
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10">
              <phase.Icon className="w-5 h-5 text-primary" strokeWidth={2} />
            </div>
            <div className="min-w-0 flex-1 pt-0.5">
              <div className="flex items-center gap-2">
                <p className="text-[13px] font-medium text-neutral-text leading-tight">
                  {phase.label}
                </p>
                <Loader2 className="w-3.5 h-3.5 text-primary/50 animate-spin shrink-0" strokeWidth={2.5} />
              </div>
              <p className="text-[11px] text-neutral-text/50 mt-0.5 leading-snug">
                {phase.sub}
              </p>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Phase dots + progress bar */}
      <div className="px-5 pb-3 flex flex-col gap-2.5">
        <div className="flex items-center justify-center gap-1.5">
          {PHASES.map((p, i) => (
            <div
              key={p.key}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === active
                  ? "w-5 bg-primary/70"
                  : "w-1.5 bg-neutral-300/60"
              }`}
            />
          ))}
        </div>
        <div className="h-0.5 w-full rounded-full bg-neutral-200/60 overflow-hidden">
          <motion.div
            className="h-full bg-primary/40 rounded-full"
            style={{ width: `${fill * 100}%` }}
          />
        </div>
      </div>

      {/* Streaming text preview */}
      <div className="mx-4 mb-4 rounded-lg bg-neutral-50/80 border border-neutral-border/30 px-3 py-2.5 overflow-hidden">
        {hasStream ? (
          <p className="text-[11px] leading-relaxed text-neutral-text/30 line-clamp-4 whitespace-pre-wrap break-words font-mono">
            {streamDisplay}
            <span className="inline-block w-[5px] h-[11px] bg-neutral-text/20 align-text-bottom ml-px animate-pulse" />
          </p>
        ) : (
          <div className="flex flex-col gap-1.5 animate-pulse">
            <div className="h-2.5 w-[85%] rounded bg-neutral-200/60" />
            <div className="h-2.5 w-[70%] rounded bg-neutral-200/60" />
            <div className="h-2.5 w-[55%] rounded bg-neutral-200/60" />
          </div>
        )}
      </div>
    </motion.div>
  );
}
